import { ApiError } from '@api/api-client';
import { importEdition, type ImportEditionResult, type ImportEditionRow } from '@api/editions-api';
import Modal from '@components/modal';
import {
  buildFailedRowsCsv,
  type EditionPreview,
  getEditionPreviews,
  parseEditionRows,
  validateEditionHeaders,
} from '@utils/csv-edition-parser';
import { downloadCsv } from '@utils/csv-utils';
import { AlertTriangle, CheckCircle, Download, FileUp, Upload, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { sileo } from 'sileo';

interface ImportEditionCsvModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ImportState = 'idle' | 'preview' | 'importing' | 'done';

export default function ImportEditionCsvModal({
  isOpen,
  onClose,
}: ImportEditionCsvModalProps): React.JSX.Element {
  const [state, setState] = useState<ImportState>('idle');
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState<ImportEditionRow[]>([]);
  const [previews, setPreviews] = useState<EditionPreview[]>([]);
  const [result, setResult] = useState<ImportEditionResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback((): void => {
    setState('idle');
    setFileName('');
    setParsedRows([]);
    setPreviews([]);
    setResult(null);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const processFile = useCallback((file: File): void => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.tsv') && !file.name.endsWith('.txt')) {
      sileo.error({ title: 'Invalid file type. Please upload a CSV/TSV file.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;

      const validationError = validateEditionHeaders(content);
      if (validationError) {
        sileo.error({ title: validationError });
        return;
      }

      const rows = parseEditionRows(content);
      if (rows.length === 0) {
        sileo.error({ title: 'No valid data rows found in the file.' });
        return;
      }

      setParsedRows(rows);
      setPreviews(getEditionPreviews(rows));
      setFileName(file.name);
      setState('preview');
    };
    reader.readAsText(file);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleImport = async (): Promise<void> => {
    setState('importing');
    try {
      const importResult = await importEdition(parsedRows);
      setResult(importResult);
      setState('done');

      if (importResult.errors.length === 0) {
        sileo.success({
          title: `Import complete: ${importResult.editionsCreated} created, ${importResult.editionsUpdated} updated`,
        });
      } else {
        sileo.warning({
          title: `Import finished with ${importResult.errors.length} warning(s)`,
        });
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Import failed';
      sileo.error({ title: message });
      setState('preview');
    }
  };

  const totalCategories = new Set(parsedRows.map((r) => r.categoryExternalId)).size;
  const totalParticipants = new Set(parsedRows.map((r) => r.dpDoctorId)).size;

  const renderIdle = (): React.JSX.Element => (
    <div
      className={`csv-drop-zone${isDragging ? ' csv-drop-zone--active' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
      }}
    >
      <Upload size={40} className="csv-drop-zone__icon" />
      <p className="csv-drop-zone__title">Drag & drop your CSV file here</p>
      <p className="csv-drop-zone__subtitle">or click to browse. Supported: .csv, .tsv, .txt</p>
      <p className="csv-drop-zone__hint">
        Creates/updates editions, participants, category assignments and nominees
      </p>
      <p className="csv-drop-zone__hint">
        Nominee key: dp_doctor_id for Doctoralia, hsid for a PMS site named in site_code
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.tsv,.txt"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );

  const renderPreview = (): React.JSX.Element => (
    <div className="csv-preview">
      <div className="csv-file-info">
        <FileUp size={20} className="csv-file-info__icon" />
        <div className="csv-file-info__details">
          <p className="csv-file-info__name">{fileName}</p>
          <p className="csv-file-info__meta">
            {previews.length} edition{previews.length !== 1 ? 's' : ''} &middot; {totalCategories}{' '}
            categor{totalCategories !== 1 ? 'ies' : 'y'} &middot; {totalParticipants} participant
            {totalParticipants !== 1 ? 's' : ''} &middot; {parsedRows.length} nomination
            {parsedRows.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-icon btn-sm"
          onClick={reset}
          aria-label="Remove file"
        >
          <X size={16} />
        </button>
      </div>

      <div className="csv-preview__table-wrapper">
        <table className="csv-preview__table">
          <thead>
            <tr>
              <th>Edition</th>
              <th>Country</th>
              <th>Site</th>
              <th>Year</th>
              <th>Categories</th>
              <th>Participants</th>
              <th>Nominations</th>
            </tr>
          </thead>
          <tbody>
            {previews.map((preview) => (
              <tr key={preview.editionExternalId}>
                <td>{preview.name}</td>
                <td>
                  <span className="badge badge-draft">{preview.countryCode}</span>
                </td>
                <td>{preview.siteCode ?? "country's SSO site"}</td>
                <td>{preview.year}</td>
                <td>{preview.categoryCount}</td>
                <td>{preview.participantCount}</td>
                <td>{preview.nomineeCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDone = (): React.JSX.Element | null => {
    if (!result) return null;

    const hasErrors = result.errors.length > 0;

    return (
      <div className="csv-result">
        <div
          className={`csv-result__banner csv-result__banner--${hasErrors ? 'warning' : 'success'}`}
        >
          {hasErrors ? (
            <AlertTriangle size={24} className="csv-result__banner-icon--warning" />
          ) : (
            <CheckCircle size={24} className="csv-result__banner-icon--success" />
          )}
          <p className="csv-result__banner-title">
            {hasErrors ? 'Import completed with warnings' : 'Import completed successfully'}
          </p>
        </div>

        <div className="csv-result__stats csv-result__stats--4">
          <div className="csv-result__stat">
            <p className="csv-result__stat-value csv-result__stat-value--primary">
              {result.editionsCreated + result.editionsUpdated}
            </p>
            <p className="csv-result__stat-label">Editions ({result.editionsCreated} new)</p>
          </div>
          <div className="csv-result__stat">
            <p className="csv-result__stat-value csv-result__stat-value--info">
              {result.participantsCreated + result.participantsUpdated}
            </p>
            <p className="csv-result__stat-label">
              Participants ({result.participantsCreated} new)
            </p>
          </div>
          <div className="csv-result__stat">
            <p className="csv-result__stat-value csv-result__stat-value--success">
              {result.categoriesAssigned}
            </p>
            <p className="csv-result__stat-label">Categories assigned</p>
          </div>
          <div className="csv-result__stat">
            <p className="csv-result__stat-value csv-result__stat-value--muted">
              {result.nomineesAssigned}
            </p>
            <p className="csv-result__stat-label">Nominees added</p>
          </div>
          {result.nomineesRemoved > 0 && (
            <div className="csv-result__stat">
              <p className="csv-result__stat-value csv-result__stat-value--danger">
                {result.nomineesRemoved}
              </p>
              <p className="csv-result__stat-label">Nominees removed</p>
            </div>
          )}
        </div>

        {hasErrors && (
          <div className="csv-result__warnings">
            <p className="csv-result__warnings-title">Warnings:</p>
            <ul className="csv-result__warnings-list">
              {result.errors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {result.failedRows.length > 0 && (
          <button
            type="button"
            className="btn btn-secondary"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => {
              const csv = buildFailedRowsCsv(result.failedRows);
              downloadCsv(csv, 'failed-import-rows.csv');
            }}
          >
            <Download size={16} />
            Download failed rows ({result.failedRows.length})
          </button>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        state === 'done'
          ? 'Import results'
          : state === 'preview'
            ? 'Review import data'
            : 'Import edition from CSV'
      }
      footer={
        state === 'done' ? (
          <button type="button" className="btn btn-primary" onClick={handleClose}>
            Close
          </button>
        ) : state === 'preview' ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={reset}>
              Back
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void handleImport()}>
              <Upload size={16} />
              Import {previews.length} edition{previews.length !== 1 ? 's' : ''}
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-secondary" onClick={handleClose}>
            Cancel
          </button>
        )
      }
    >
      {state === 'idle' && renderIdle()}
      {state === 'preview' && renderPreview()}
      {state === 'importing' && (
        <div className="csv-importing">
          <p className="csv-importing__title">Importing...</p>
          <p className="csv-importing__subtitle">
            Processing editions, participants, categories and nominees
          </p>
        </div>
      )}
      {state === 'done' && renderDone()}
    </Modal>
  );
}
